import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Merge } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, StageBadge } from "@/components/Layout";
import { api, errMsg, fullName, fmtDate } from "@/lib/api";

function Group({ g, onMerged }) {
  const [keep, setKeep] = useState(g.leads[0].id);
  const [busy, setBusy] = useState(false);
  const merge = async () => {
    if (!window.confirm(`Merge ${g.leads.length} leads into the selected record? The others are deleted after their details, notes and activity are combined.`)) return;
    setBusy(true);
    try { await api.post("/leads/merge", { keep_id: keep, merge_ids: g.leads.map((l) => l.id) }); toast.success(`Merged ${g.email}`); onMerged(g.email); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border rounded-xl p-4 space-y-3" data-testid={`dup-group-${g.email}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="font-semibold">{g.email} <span className="label-xs ml-2">{g.leads.length} leads</span></div>
        <Button size="sm" disabled={busy} onClick={merge} data-testid={`dup-merge-${g.email}`}><Merge size={14} className="mr-1" />Merge into selected</Button>
      </div>
      <div className="grid gap-2">
        {g.leads.map((l) => (
          <label key={l.id} className={`flex flex-wrap items-center gap-3 border rounded-lg p-3 cursor-pointer transition-colors ${keep === l.id ? "border-blue-500 bg-blue-50/50" : "hover:bg-slate-50"}`} data-testid={`dup-keep-${l.id}`}>
            <input type="radio" name={`keep-${g.email}`} checked={keep === l.id} onChange={() => setKeep(l.id)} />
            <span className="w-56"><Link to={`/leads/${l.id}`} className="font-medium hover:text-blue-600" onClick={(e) => e.stopPropagation()}>{l.company_name}</Link><span className="block text-xs text-slate-400">{fullName(l) || "No contact"}</span></span>
            <StageBadge stage={l.stage} />
            <span className="text-xs text-slate-500">{(l.business_categories || []).join(", ") || "No category"}</span>
            <span className="text-xs text-slate-500">{l.telephone || ""} {l.website || ""}</span>
            <span className="ml-auto text-xs text-slate-400">Score {l.score || 0} · added {fmtDate(l.created_at)}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export default function Duplicates() {
  const [groups, setGroups] = useState(null);
  useEffect(() => { api.get("/leads/duplicates").then((r) => setGroups(r.data)); }, []);
  return (
    <div>
      <PageHeader eyebrow="Leads" title="Duplicate leads" />
      <div className="p-6 lg:p-10 space-y-4 max-w-5xl">
        <p className="text-sm text-slate-500">Leads that share an email address. Pick the record to keep and click Merge. Empty fields are filled from the other records; notes, activity, tasks, categories and scores are combined, and the furthest-along stage is kept.</p>
        {groups?.map((g) => <Group key={g.email} g={g} onMerged={(e) => setGroups((x) => x.filter((y) => y.email !== e))} />)}
        {groups && !groups.length && <div className="bg-white border rounded-xl p-10 text-center text-sm text-slate-500" data-testid="dup-empty">No duplicate emails found.</div>}
      </div>
    </div>
  );
}
